import { Box, Typography } from "@outschool/backpack";
import {
  ActivityEnrollQueryQuery,
  ParentChildrenQuery
} from "@outschool/gql-frontend-generated";
import { dayjs } from "@outschool/time";
import _ from "lodash";
import React from "react";
import ReactSelect from "react-select";

import { useOrderContext } from "../OrderContext";

// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

type OngoingClassMeetingInputsProps = {
  startMeetingLabel?: string;
  endMeetingLabel?: string;
  shouldShowStartMeeting: boolean;
  shouldShowEndMeeting: boolean;
  currentEnrollments?: NonNullable<
    ParentChildrenQuery["parentByEmail"]
  >["enrollments"];
  section?: NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["section"];
  selectedLearners?: string[];
};

export const OngoingClassMeetingInputs: React.FunctionComponent<
  OngoingClassMeetingInputsProps
> = ({
  startMeetingLabel = "First week of class",
  endMeetingLabel = "Last week of class",
  shouldShowStartMeeting,
  shouldShowEndMeeting,
  currentEnrollments,
  section,
  selectedLearners
}) => {
  return (
    <>
      {shouldShowStartMeeting && (
        <StartMeetingInput label={startMeetingLabel} />
      )}
      {shouldShowEndMeeting && (
        <EndMeetingInput
          label={endMeetingLabel}
          currentEnrollments={currentEnrollments}
          section={section}
          selectedLearners={selectedLearners}
        />
      )}
    </>
  );
};

/* EndMeetingInput */

type EndMeetingInputProps = {
  label: string;
  currentEnrollments?: NonNullable<
    ParentChildrenQuery["parentByEmail"]
  >["enrollments"];
  section?: NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["section"];
  selectedLearners?: string[];
};

const EndMeetingInput: React.FunctionComponent<EndMeetingInputProps> = ({
  label,
  currentEnrollments,
  section,
  selectedLearners
}) => {
  const {
    ongoingStartTime,
    setOngoingStartTime,
    ongoingEndTime,
    setOngoingEndTime,
    // @ts-ignore TS(2339): Property 'endOfPeriodOptions' does not exist on ty... Remove this comment to see the full error message
    prepaidOngoingPeriodOptions: { endOfPeriodOptions }
  } = useOrderContext();

  const preexistingEnrollments = currentEnrollments?.filter(
    enrollment =>
      enrollment?.section_uid === section?.uid &&
      selectedLearners?.includes(enrollment?.learner?.uid ?? "")
  );
  const preexistingEnrollment = _.sortBy(
    preexistingEnrollments,
    enrollment => enrollment?.ongoingEndTime
  )[0];
  const preexistingEnrollmentEndTime = preexistingEnrollment?.ongoingEndTime;
  const filteredPrepayOptions = endOfPeriodOptions.filter(
    (option: MeetingDaySelectOption) =>
      option.value > preexistingEnrollmentEndTime ||
      !preexistingEnrollmentEndTime
  );

  return (
    <MeetingDayInput
      label={label}
      options={filteredPrepayOptions}
      handleChange={option => {
        // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        setOngoingEndTime(option.value);
        // if end time is before start, clear start time
        if (
          ongoingStartTime &&
          dayjs(option.value).isBefore(ongoingStartTime)
        ) {
          // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
          setOngoingStartTime(null);
        }
      }}
      value={
        ongoingEndTime
          ? endOfPeriodOptions.find(
              (option: $TSFixMe) => option.value === ongoingEndTime
            )
          : null
      }
    />
  );
};

/* StartMeetingInput */

type StartMeetingInputProps = { label: string };

const StartMeetingInput: React.FunctionComponent<StartMeetingInputProps> = ({
  label
}) => {
  const {
    ongoingStartTime,
    setOngoingStartTime,
    ongoingEndTime,
    setOngoingEndTime,
    // @ts-ignore TS(2339): Property 'startOfPeriodOptions' does not exist on ... Remove this comment to see the full error message
    prepaidOngoingPeriodOptions: { startOfPeriodOptions }
  } = useOrderContext();

  return (
    <MeetingDayInput
      label={label}
      options={startOfPeriodOptions}
      handleChange={option => {
        // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        setOngoingStartTime(option.value);
        // if start time is after end time, clear end time
        if (ongoingEndTime && dayjs(option.value).isAfter(ongoingEndTime)) {
          // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
          setOngoingEndTime(null);
        }
      }}
      value={
        ongoingStartTime
          ? startOfPeriodOptions.find(
              (option: $TSFixMe) => option.value === ongoingStartTime
            )
          : null
      }
    />
  );
};

/* MeetingDayInput */

export type MeetingDaySelectOption = {
  label: string;
  value: Date;
};

type MeetingDayInputProps = {
  label: string;
  options: MeetingDaySelectOption[];
  handleChange: (option: MeetingDaySelectOption) => void;
  defaultValue?: MeetingDaySelectOption;
  value: MeetingDaySelectOption | null;
};

const MeetingDayInput: React.FunctionComponent<MeetingDayInputProps> = ({
  label,
  options,
  handleChange,
  defaultValue,
  value
}) => (
  <Box
    sx={{
      marginTop: "1em"
    }}
  >
    <Typography
      variant="h6"
      sx={{
        my: "0.5em"
      }}
    >
      {label}
    </Typography>
    <ReactSelect
      options={options}
      onChange={(option: MeetingDaySelectOption) => handleChange(option)}
      defaultValue={defaultValue}
      value={value}
    />
  </Box>
);
